import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { setUserId } from "../../lib/utm";
import { useLoans, isCreditSeaSmart } from "../../components/loans/LoansContext";
import { Breadcrumb, PageTitle, GradButton } from "../../components/loans/LoansUi";
import { useSeo } from "../LoansLanding";

const PERKS = ["Know your loan eligibility instantly", "Get offers from trusted lenders", "Quick and secure process"];
const inputCls = "w-full h-14 rounded-xl border border-slate-200 bg-white px-4 text-[15px] outline-none placeholder:text-slate-400 focus:border-[#10B981] focus:ring-4 focus:ring-emerald-100 transition-shadow";

export default function LoansEligibility() {
  useSeo("Check Loan Eligibility · loanexpress", "Share a few details to check your personal loan eligibility and get the best offers from our lending partners.");
  const [params] = useSearchParams();
  const { apply } = useLoans();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [income, setIncome] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const clean = phone.replace(/\D/g, "").slice(-10);
    if (!name.trim()) return toast.error("Enter your full name", { id: "elig" });
    if (!/^[6-9]\d{9}$/.test(clean)) return toast.error("Enter a valid 10-digit mobile number", { id: "elig" });
    const inc = Number(income.replace(/\D/g, ""));
    if (!inc) return toast.error("Enter your monthly income", { id: "elig" });
    setLoading(true);
    try {
      const amt = Number(params.get("amount")) || undefined;
      const { data } = await api.post("/loans/match", { name: name.trim(), phone: clean, monthly_income: inc, loan_amount: amt });
      if (data.hitch_user_id) setUserId(data.hitch_user_id);
      const best = data.top || data.matches?.[0];
      if (!best) { toast.info("No lenders available right now"); return; }
      if (isCreditSeaSmart(best)) { apply(best); return; }
      toast.success(`Best match: ${best.provider} — redirecting…`);
      window.location.href = best.tracked_redirect_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-5 md:px-10 pt-10 pb-20">
      <Breadcrumb items={[{ label: "Check Eligibility" }]} />
      <div className="mt-10"><PageTitle plain="Check Loan" accent="Eligibility" sub="Share a few details to check your personal loan eligibility and get best offers from our lending partners." /></div>
      <div className="mt-12 grid lg:grid-cols-[1.6fr_1fr] gap-8">
        <form onSubmit={submit} data-testid="le-elig-form" className="bg-white rounded-3xl border border-slate-100 shadow-[0_20px_60px_-25px_rgba(15,40,90,0.2)] p-7 md:p-10 space-y-6">
          <div>
            <label className="font-semibold">Full Name</label>
            <input data-testid="le-elig-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your full name" className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <label className="font-semibold">Mobile Number</label>
            <div className="mt-2 flex gap-3">
              <span className="w-24 h-14 rounded-xl border border-slate-200 grid place-items-center font-semibold">+91</span>
              <input data-testid="le-elig-phone" type="tel" inputMode="numeric" maxLength={10} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} placeholder="Enter 10-digit mobile number" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="font-semibold">Monthly Income (₹)</label>
            <input data-testid="le-elig-income" inputMode="numeric" value={income ? Number(income).toLocaleString("en-IN") : ""} onChange={(e) => setIncome(e.target.value.replace(/\D/g, "").slice(0, 9))} placeholder="Enter your monthly income" className={`mt-2 ${inputCls}`} />
          </div>
          <GradButton type="submit" data-testid="le-elig-submit" disabled={loading} className="w-full h-14 text-lg">
            {loading ? "Checking…" : <>Check Eligibility <ArrowRight className="w-5 h-5" /></>}
          </GradButton>
          <p className="text-xs text-slate-400 text-center">No impact on your credit score. By continuing you agree to be contacted by our lending partners.</p>
        </form>
        <div className="rounded-3xl bg-[#ECFAF4] border border-slate-100 p-8 md:p-10 flex flex-col justify-center gap-10">
          {PERKS.map((p) => (
            <div key={p} className="flex items-start gap-4 text-xl font-medium"><Check className="w-7 h-7 text-[#10A37F] shrink-0" strokeWidth={3} />{p}</div>
          ))}
        </div>
      </div>
    </section>
  );
}
